/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FieldArray, FormikProps } from 'formik';
import { debounce } from 'lodash';
import { memo, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  OutlinedInput,
  Tooltip,
  Typography,
} from '@mui/material';

import { CIColors } from 'shared';

import { OutlinedPasswordInput } from '../../../../elements/OutlinedPasswordInput/OutlinedPasswordInput';
import { Namespaces } from '../../../../i18n';
import { useFormStyles } from '../../../../styles/form';
import { OidcConfiguration } from '../../../../types/organization/OidcConfiguration';
import { useObjectState } from '../../../../utils/useObjectState';
import { useValidationTranslation } from '../../../../utils/useValidationTranslation';
import { useSsoTabStyles } from './SsoTab.styles';
import { OidcConfigurationValues } from './SsoTab.types';

type SsoTabFormProps = FormikProps<OidcConfigurationValues> & {
  oidcConfig?: OidcConfiguration;
  setIsOpenModal: (val: boolean) => void;
  isFetching: boolean;
};

type IssuerUrlValidationState = {
  isLoading: boolean;
  isSuccessful: boolean;
  isError: boolean;
};

export const SsoTabForm = memo<SsoTabFormProps>(props => {
  const { oidcConfig, setIsOpenModal, isFetching, ...formProps } = props;
  const { handleSubmit, values, handleChange, handleBlur, errors, touched, dirty } = formProps;

  const classes = useSsoTabStyles();
  const formClasses = useFormStyles();

  const { t } = useTranslation(Namespaces.OrganizationAdmin);
  const { vtr } = useValidationTranslation();

  const [issuerValidation, setIssuerValidation] = useObjectState<IssuerUrlValidationState>({
    isLoading: false,
    isSuccessful: false,
    isError: false,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedIssuerCheck = useCallback(
    debounce(async val => {
      if (errors.issuerUrl) {
        setIssuerValidation({
          isLoading: false,
          isSuccessful: false,
          isError: false,
        });
      } else {
        if (val) {
          setIssuerValidation({
            isLoading: true,
            isSuccessful: false,
            isError: false,
          });

          await fetch(`${val}/.well-known/openid-configuration`)
            .then(() => {
              setTimeout(() => {
                setIssuerValidation({
                  isLoading: false,
                  isSuccessful: true,
                  isError: false,
                });
              }, 1000);
            })
            .catch(() => {
              setTimeout(() => {
                setIssuerValidation({
                  isLoading: false,
                  isSuccessful: false,
                  isError: true,
                });
              }, 1000);
            });
        }
      }
    }, 500),
    [errors.issuerUrl],
  );

  const hasError = (field: keyof Omit<OidcConfigurationValues, 'domains'>): boolean => {
    return !!touched[field] && !!errors[field];
  };

  const getError = (field: keyof Omit<OidcConfigurationValues, 'domains'>): string => {
    return errors[field] || '';
  };

  const domainHasError = (index: number) => {
    if (Array.isArray(errors.domains)) {
      return !!touched.domains && !!errors.domains[index];
    }
    return false;
  };

  const domainGetError = (index: number) => {
    if (Array.isArray(errors['domains'])) {
      return errors['domains'][index] || '';
    }
    return '';
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={classes.root}>
        {oidcConfig && (
          <div className={classes.statusContainer}>
            <Typography data-cy="manage-organization-oidc-status">
              <Trans
                i18nKey={`${Namespaces.OrganizationAdmin}:oidc.status.label`}
                values={{ status: oidcConfig.enabled ? t('oidc.status.active') : t('oidc.status.inactive') }}
                components={{
                  status: (
                    <b style={{ color: oidcConfig.enabled ? CIColors.Success : CIColors.Error, fontWeight: 400 }} />
                  ),
                }}
              />
            </Typography>
            <Button
              variant="outlined"
              color={oidcConfig.enabled ? 'error' : 'success'}
              onClick={() => setIsOpenModal(true)}
              data-cy="manage-organization-oidc-status-change"
              startIcon={oidcConfig.enabled ? <PauseIcon /> : <PlayArrowIcon />}
            >
              {oidcConfig.enabled ? t('oidc.status.actions.deactivate') : t('oidc.status.actions.activate')}
            </Button>
          </div>
        )}
        <FormControl
          variant="outlined"
          error={hasError('issuerUrl') || issuerValidation.isError}
          className={cx(formClasses.formControl, formClasses.widthMedium, classes.helperText)}
        >
          <InputLabel htmlFor="identifier">{t('oidc.properties.issuerUrl.title')}</InputLabel>
          <OutlinedInput
            id="issuerUrl"
            name="issuerUrl"
            label={t('oidc.properties.issuerUrl.title')}
            fullWidth
            type="text"
            value={values.issuerUrl}
            onChange={e => {
              handleChange(e);
              void debouncedIssuerCheck(e.target.value);
            }}
            onBlur={handleBlur}
            disabled={isFetching || !values.enabled}
            data-cy="manage-organization-oidc-issuer-url"
            endAdornment={
              <>
                {issuerValidation.isLoading && <CircularProgress size={22.28} />}
                {issuerValidation.isSuccessful && (
                  <Tooltip arrow title={t('oidc.properties.issuerUrl.success')} sx={{ cursor: 'default' }}>
                    <CheckIcon data-cy="sso-tab-issuer-url-success-icon" color="success" />
                  </Tooltip>
                )}
                {issuerValidation.isError && (
                  <Tooltip
                    arrow
                    title={
                      <Trans i18nKey={`organizationAdmin:oidc.properties.issuerUrl.error`} components={{ b: <b /> }} />
                    }
                    sx={{ cursor: 'default' }}
                  >
                    <CloseIcon data-cy="sso-tab-issuer-url-error-icon" color="error" />
                  </Tooltip>
                )}
              </>
            }
          />
          {hasError('issuerUrl') && (
            <FormHelperText>
              {vtr(getError('issuerUrl'), {
                field: t('oidc.properties.issuerUrl.title'),
              })}
            </FormHelperText>
          )}
          <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>
            <Trans
              i18nKey={`${Namespaces.OrganizationAdmin}:oidc.properties.issuerUrl.hint`}
              components={{ b: <b /> }}
            />
          </FormHelperText>
        </FormControl>
        <FormControl
          variant="outlined"
          error={hasError('clientId')}
          className={cx(formClasses.formControl, formClasses.widthMedium, classes.helperText)}
        >
          <InputLabel htmlFor="clientId">{t('oidc.properties.clientId.title')}</InputLabel>
          <OutlinedInput
            id="clientId"
            name="clientId"
            label={t('oidc.properties.clientId.title')}
            fullWidth
            type="text"
            value={values.clientId}
            onChange={handleChange}
            onBlur={handleBlur}
            error={hasError('clientId')}
            disabled={isFetching || !values.enabled}
            data-cy="manage-organization-oidc-client-id"
          />
          {hasError('clientId') && (
            <FormHelperText>
              {vtr(getError('clientId'), {
                field: t('oidc.properties.clientId.title'),
              })}
            </FormHelperText>
          )}
          <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>
            {t('oidc.properties.clientId.hint')}
          </FormHelperText>
        </FormControl>
        <FormControl
          variant="outlined"
          error={hasError('clientSecret')}
          className={cx(formClasses.formControl, formClasses.widthMedium, classes.helperText)}
        >
          <InputLabel htmlFor="clientSecret">{t('oidc.properties.clientSecret.title')}</InputLabel>
          <OutlinedPasswordInput
            id="clientSecret"
            name="clientSecret"
            label={t('oidc.properties.clientSecret.title')}
            fullWidth
            value={values.clientSecret}
            onChange={handleChange}
            onBlur={handleBlur}
            error={hasError('clientSecret')}
            disabled={isFetching || !values.enabled}
            data-cy="manage-organization-oidc-client-secret"
          />
          {hasError('clientSecret') && (
            <FormHelperText>
              {vtr(getError('clientSecret'), {
                field: t('oidc.properties.clientSecret.title'),
              })}
            </FormHelperText>
          )}
          <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>
            {t('oidc.properties.clientSecret.hint')}
          </FormHelperText>
        </FormControl>
        <FormControl
          variant="outlined"
          error={hasError('groupAttribute')}
          className={cx(formClasses.formControl, formClasses.widthMedium, classes.helperText)}
        >
          <InputLabel htmlFor="groupAttribute">{t('oidc.properties.groupAttribute.title')}</InputLabel>
          <OutlinedInput
            id="groupAttribute"
            name="groupAttribute"
            label={t('oidc.properties.groupAttribute.title')}
            fullWidth
            type="text"
            value={values.groupAttribute}
            onChange={handleChange}
            onBlur={handleBlur}
            error={hasError('groupAttribute')}
            disabled={isFetching || !values.enabled}
            data-cy="manage-organization-oidc-group-attribute"
          />
          {hasError('groupAttribute') && (
            <FormHelperText>
              {vtr(getError('groupAttribute'), {
                field: t('oidc.properties.groupAttribute.title'),
              })}
            </FormHelperText>
          )}
          <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>
            {t('oidc.properties.groupAttribute.hint')}
          </FormHelperText>
        </FormControl>
        <FieldArray
          name="domains"
          render={helpers => (
            <div className={classes.domainsRoot}>
              <Typography color={typeof helpers.form.errors['domains'] === 'string' ? 'error' : 'text'}>
                {t('oidc.properties.domains.title')}:
              </Typography>
              {typeof helpers.form.errors['domains'] === 'string' && (
                <Typography className={cx(classes.domainsError, classes.domainsHint, formClasses.widthMedium)}>
                  {vtr(helpers.form.errors['domains'], {
                    field: t('oidc.properties.domains.title'),
                  })}
                </Typography>
              )}
              <Typography className={cx(classes.domainsHint, formClasses.widthMedium)}>
                <Trans
                  i18nKey={`${Namespaces.OrganizationAdmin}:oidc.properties.domains.hint`}
                  components={{ b: <b /> }}
                />
              </Typography>
              <div className={classes.domains}>
                {values.domains.map((val, idx) => (
                  <div key={idx} className={classes.domain}>
                    <FormControl
                      variant="outlined"
                      className={cx(formClasses.formControl, formClasses.widthDefault, classes.helperText)}
                      error={domainHasError(idx)}
                    >
                      <OutlinedInput
                        name={`domains.${idx}`}
                        placeholder="example.com"
                        fullWidth
                        value={val}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        data-cy={`manage-organization-oidc-domain-${idx}`}
                        disabled={isFetching || !values.enabled}
                      />
                      {domainHasError(idx) && (
                        <FormHelperText>
                          {vtr(domainGetError(idx), {
                            field: t('oidc.properties.domains.field'),
                          })}
                        </FormHelperText>
                      )}
                    </FormControl>
                    <Tooltip title={t('oidc.properties.domains.actions.remove')}>
                      <IconButton
                        onClick={() => helpers.remove(idx)}
                        data-cy="manage-organization-oidc-remove-domain"
                        disabled={isFetching || !values.enabled}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                ))}
                <Button
                  onClick={() => helpers.push('')}
                  sx={{ mt: '5px' }}
                  startIcon={<AddIcon />}
                  data-cy="manage-organization-oidc-add-domain"
                  disabled={isFetching || !values.enabled}
                >
                  {t('oidc.properties.domains.actions.add')}
                </Button>
              </div>
            </div>
          )}
        />
        <div className={cx(classes.switchContainer, formClasses.widthMedium)}>
          <FormControl>
            <FormControlLabel
              name="verifyCertificate"
              checked={values.verifyCertificate}
              onChange={handleChange}
              control={<Checkbox />}
              label={t('oidc.properties.verifyCertificate.title')}
              labelPlacement="end"
              disabled={isFetching || !values.enabled}
              data-cy="manage-organization-oidc-verify-certificate"
            />
            <FormHelperText className={classes.switchHint}>
              {t('oidc.properties.verifyCertificate.hint')}
            </FormHelperText>
          </FormControl>
        </div>
      </div>
      <Button
        type="submit"
        variant="contained"
        sx={{ mt: '20px' }}
        data-cy="manage-organization-oidc-submit"
        disabled={isFetching || !values.enabled || !dirty}
      >
        {t(`${Namespaces.Admin}:buttons.submit`)}
      </Button>
    </form>
  );
});
